import {create} from 'apisauce';
import qs from 'qs';
import {
  AlertService,
  AmbientWeatherService,
  AppConfigService,
  AuthService,
  BillingService,
  DavisService,
  EcowittService,
  ForecastMapService,
  GoogleService,
  MyAcuriteService,
  NetatmoEnergyService,
  NetatmoWeatherService,
  PlacesService,
  UserService,
  WeatherService,
  WARNINGS_SUMMARY_MAX_POINTS,
  fetchWarningsSummary,
  WeatherflowService,
  WundergroundService,
} from './actions';
import * as ErrorCode from './errorCodes';

export type AppPlatform = 'ios' | 'android' | 'web';

/** What the app says it is, sent on every API call (#379, backend #269). */
export type AppInfo = {
  platform: AppPlatform;
  /** Store version, e.g. "1.64.0". The webapp has none (it is always current). */
  version?: string;
  /** Build number on mobile, the commit on the webapp. */
  build?: string;
};

/**
 * The names the native widgets and native token refresh already send
 * (`SmartMixinHeaders.kt`, `SessionRefreshTransport.swift`), so the backend
 * reads one set of headers whoever calls. Each must be in the backend's
 * CORS_ALLOW_HEADERS, or every webapp request fails preflight.
 */
export const APP_INFO_HEADERS = {
  version: 'X-SmartMixin-Version',
  build: 'X-SmartMixin-BuildNumber',
  platform: 'X-SmartMixin-Platform',
} as const;

let configuredAppInfo: AppInfo | null = null;

/**
 * Configure the app identity sent on every API call. Call it at startup,
 * before the first request; `null` stops sending it. Unconfigured, no
 * identity header is sent, and the server treats the client as old.
 */
export const configureAppInfo = (appInfo: AppInfo | null) => {
  configuredAppInfo = appInfo;
};

export const getAppInfo = (): AppInfo | null => configuredAppInfo;

const apiClient = (url_root: string) => {
  const client = create({
    baseURL: `${url_root}/api`,
    timeout: 60000,
    paramsSerializer: function (params) {
      return qs.stringify(params, {arrayFormat: 'repeat'});
    },
  });
  // Every apisauce client in the apps comes from here, so this one transform
  // covers them all. Read at request time: configuring after a client exists
  // still applies.
  client.addRequestTransform((request) => {
    const appInfo = configuredAppInfo;
    if (!appInfo) {
      return;
    }
    // apisauce hands each request a fresh plain object (sticky headers merged
    // with the call's), so adding to it leaks nowhere.
    request.headers = {
      ...request.headers,
      [APP_INFO_HEADERS.platform]: appInfo.platform,
      ...(appInfo.version && {[APP_INFO_HEADERS.version]: appInfo.version}),
      ...(appInfo.build && {[APP_INFO_HEADERS.build]: appInfo.build}),
    } as typeof request.headers;
  });
  return client;
};

export {
  AlertService,
  AmbientWeatherService,
  AppConfigService,
  AuthService,
  BillingService,
  DavisService,
  EcowittService,
  ErrorCode,
  ForecastMapService,
  GoogleService,
  MyAcuriteService,
  NetatmoEnergyService,
  NetatmoWeatherService,
  PlacesService,
  UserService,
  WeatherService,
  WARNINGS_SUMMARY_MAX_POINTS,
  fetchWarningsSummary,
  WeatherflowService,
  WundergroundService,
  apiClient,
};
export type {
  AppPlatformWithPolicy,
  PublicConfig,
  PublicFlagsResponse,
  WarningsSummaryPoint,
} from './actions';
export {parsePublicConfig} from './actions';

export const SOURCE_SYNOP = 0;
export const SOURCE_NETATMO_WEATHER = 1;
export const SOURCE_DAVIS = 2;
export const SOURCE_WEATHERFLOW = 3;
export const SOURCE_AMBIENT_WEATHER = 4;
export const SOURCE_WUNDERGROUND = 5;
export const SOURCE_ECOWITT = 6;
export const SOURCE_MY_ACURITE = 7;

export const SUPPORTED_API_VERSION = 7;
const DEFAULT_URL_ROOT = 'https://api2.smartmixin.io';

let configuredUrlRoot: string | null = null;

/**
 * Configure the API URL at runtime.
 * Must be called before any API calls are made (typically at app startup).
 * If not called, defaults to production URL.
 */
export const configureApiUrl = (url: string) => {
  configuredUrlRoot = url;
};

/**
 * Get the current API URL.
 * Returns configured URL if set, otherwise the default production URL.
 */
export const getApiUrl = (): string => {
  return configuredUrlRoot ?? DEFAULT_URL_ROOT;
};

let configuredDeviceId: string | null = null;

/**
 * Configure the device id sent as the X-Device-Id header on every API call.
 * Mobile-only (webapp never calls this): the backend links pre-account store
 * purchases to this device, which is how anonymous premium is recognized
 * server-side (e.g. the premium-gated records endpoint).
 */
export const configureDeviceId = (deviceId: string) => {
  configuredDeviceId = deviceId;
};

export const getDeviceId = (): string | null => {
  return configuredDeviceId;
};

export {
  REFRESHER_TIMEOUT_MS,
  configureTokenRefresher,
  getTokenRefresher,
} from './tokenRefresher';
export type {
  TokenRefresher,
  TokenRefreshRequest,
  TokenRefreshResult,
  TransientRefreshCode,
} from './tokenRefresher';

// Legacy export for backward compatibility
export const URL_ROOT = DEFAULT_URL_ROOT;
